import { makeStyles } from '@material-ui/core/styles';
import { mdiClose, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import Button from 'components/Button/Button';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectProjectAssignmentsWithAssignmentNumberPath } from 'store/assignments/selectors/assignment.selectors';
import {
  setIsMediaTagInputActive,
  setMediaTagInputValue,
} from 'store/mediaTags/actions/mediaTags.actions';
import {
  selectGroupedProjectMediaTags,
  selectIsCreatingOrUpdatingTags,
  selectMediaTagInputValue,
  selectSelectedProjectMediaTags,
} from 'store/mediaTags/selectors/mediaTags.selectors';
import { addMediaTagForFile } from 'store/mediaTags/thunks/addMediaTagForFile.thunk';
import { createProjectMediaTag } from 'store/mediaTags/thunks/createProjectMediaTag.thunk';
import { selectSelectedProjectId } from 'store/projects/selectors/projectsSelectors';
import { MediaTagAutocomplete } from './MediaTagAutoComplete';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    width: 300,
    // On a phone the field gets a line of its own in the action bar (see
    // ImageMediaTags), so it may as well use all of it.
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  closeButton: {
    marginLeft: 4,
    flexShrink: 0,
  },
}));

export const MediaTagInput = () => {
  const classes = useStyles();
  const selectedProjectMediaTags = useSelector(selectSelectedProjectMediaTags);

  const projectMediaTagOptions = useSelector(selectGroupedProjectMediaTags);

  const mediaTagInputValue = useSelector(selectMediaTagInputValue);

  const isCreatingOrUpdatingMediaTags = useSelector(
    selectIsCreatingOrUpdatingTags
  );

  const selectedProjectId = useSelector(selectSelectedProjectId);

  const selectedProjectAssigments = useSelector((state) =>
    selectProjectAssignmentsWithAssignmentNumberPath(state, selectedProjectId)
  );

  const dispatch = useDispatch();

  const { t } = useTranslation('common');

  const handleCreateTag = () => {
    const alreadyFoundTagWithName = selectedProjectMediaTags.find(
      (mediaTag) =>
        mediaTag.name.toLowerCase() === mediaTagInputValue.toLowerCase()
    );

    if (alreadyFoundTagWithName) {
      dispatch(addMediaTagForFile(alreadyFoundTagWithName.mediaTagId));
    } else {
      dispatch(createProjectMediaTag());
    }
  };

  const handleSelectMediaTag = (selectedMediaTag) => {
    const nameValue = selectedMediaTag.name;

    dispatch(setMediaTagInputValue(nameValue));
    const foundSelectedTag = selectedProjectMediaTags.find(
      (mediaTag) => mediaTag.name === nameValue
    );

    if (foundSelectedTag) {
      dispatch(addMediaTagForFile(foundSelectedTag.mediaTagId));
    } else {
      const foundSelectedValueFromAssignments = selectedProjectAssigments.find(
        (assignment) => assignment.name === nameValue
      );

      if (foundSelectedValueFromAssignments) {
        dispatch(
          createProjectMediaTag({
            name: foundSelectedValueFromAssignments.name,
            assignmentId: foundSelectedValueFromAssignments.id,
            assignmentNumberPath:
              foundSelectedValueFromAssignments.assignmentNumberPath,
          })
        );
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleCreateTag();
  };

  // Closing discards whatever was typed, so reopening starts clean.
  const handleClose = () => {
    dispatch(setMediaTagInputValue(''));
    dispatch(setIsMediaTagInputActive(false));
  };

  // The autocomplete swallows Escape while its list is open, so the first
  // press closes the list and the next one closes the field.
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClose();
    }
  };

  return (
    <form
      className={classes.form}
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
    >
      <MediaTagAutocomplete
        mediaTagInputValue={mediaTagInputValue}
        mediaTagOptions={projectMediaTagOptions}
        handleSelectMediaTag={handleSelectMediaTag}
        setMediaTagInputValue={(value) =>
          dispatch(setMediaTagInputValue(value))
        }
      ></MediaTagAutocomplete>
      <Button
        type="submit"
        disabled={!mediaTagInputValue || isCreatingOrUpdatingMediaTags}
      >
        <Icon path={mdiPlus} size={1}></Icon>
      </Button>
      <Button
        type="button"
        outline
        color="input"
        className={classes.closeButton}
        onClick={handleClose}
        aria-label={t('common.close')}
        title={t('common.close')}
      >
        <Icon path={mdiClose} size={1}></Icon>
      </Button>
    </form>
  );
};
